import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actionLayerUpdate } from "./LayerRedux";

import Radio from "@mui/material/Radio";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import Collapse from "@mui/material/Collapse";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import IconButton from "@mui/material/IconButton";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import InfoIcon from "@mui/icons-material/Info";

const root_ = (theme) => ({
  padding: "0px",
  "& .MuiRadio-root": {
    padding: "0px",
  },
  "& .MuiTypography-overline": {
    lineHeight: 1,
  },
});

const label_ = (theme) => ({
  display: "flex",
  alignItems: "center",
});

const radioContainer_ = (theme) => ({
  textAlign: "center",
});

const satelliteInfo_ = (theme) => ({
  margin: "5px",
});

const iconButton_ = (theme) => ({
  marginInlineStart: "10px",
});

export default function GraphicLayers({ layers }) {
  return (
    <Grid container>
      <Grid size={{ xs: 6 }}></Grid>
      <Grid container size={{ xs: 6 }} style={{ textAlign: "center" }}>
        <Grid size={{ xs: 4 }}>
          <Typography variant="overline" style={{ fontWeight: "bold" }}>
            Auto
          </Typography>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Typography variant="overline" style={{ fontWeight: "bold" }}>
            Show
          </Typography>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Typography variant="overline" style={{ fontWeight: "bold" }}>
            Hide
          </Typography>
        </Grid>
      </Grid>
      {layers
        .filter((layer) => layer.type === "graphics")
        .map((layer) => (
          <GraphicLayer layer={layer} key={layer.id} />
        ))}
    </Grid>
  );
}

function GraphicLayer({ layer }) {
  const { id, label } = layer;
  const selectedValue = useSelector((state) => state.layerReducer.graphics[id]);
  const basemapSource = useSelector(
    (state) => state.layerReducer.basemapSource,
  );
  const [expanded, setExpanded] = useState(false);
  const dispatch = useDispatch();

  const handleChange = (event) => {
    const mode = event.target.value;
    const data = { id, mode, basemapSource };
    dispatch(actionLayerUpdate({ type: "graphic", data }));
  };

  return (
    <>
      <Grid container size={{ xs: 12 }} sx={root_}>
        <Grid size={{ xs: 6 }} sx={label_}>
          <Typography
            variant="overline"
            data-testid={`layer-graphic-label-${id}`}
          >
            {label}
          </Typography>
          {layer.id === "basemap" ? (
            <IconButton
              aria-label="Expand basemap options"
              size="small"
              onClick={() => setExpanded(!expanded)}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          ) : (
            ""
          )}
        </Grid>
        <Grid container size={{ xs: 6 }} sx={radioContainer_}>
          <Grid size={{ xs: 4 }}>
            <Radio
              slotProps={{
                input: {
                  "aria-label": `Auto ${id}`,
                },
              }}
              // Avoid a Siteimprove complaint. Without this, it would generate
              // <span aria-disabled="true">
              // which generates: ARIA attribute unsupported or prohibited
              aria-disabled={undefined}
              disabled={id === "basemap" || id === "building"}
              color="primary"
              checked={selectedValue === "auto"}
              onChange={handleChange}
              value="auto"
              name={"toggle-graphic-" + id}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Radio
              slotProps={{
                input: {
                  "aria-label": `Show ${id}`,
                },
              }}
              color="primary"
              checked={selectedValue === "show"}
              onChange={handleChange}
              value="show"
              name={"toggle-graphic-" + id}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Radio
              slotProps={{
                input: {
                  "aria-label": `Hide ${id}`,
                },
              }}
              color="primary"
              checked={selectedValue === "hide"}
              onChange={handleChange}
              value="hide"
              name={"toggle-graphic-" + id}
            />
          </Grid>
        </Grid>
      </Grid>
      {layer.id === "basemap" ? <BasemapSubSelector expanded={expanded} /> : ""}
    </>
  );
}

function BasemapSubSelector({ expanded }) {
  const basemapVisibility = useSelector(
    (state) => state.layerReducer.graphics.basemap,
  );
  const basemapValue = useSelector((state) => state.layerReducer.basemapSource);
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleChange = (event) => {
    const bmap = event.target.value;
    const data = {
      id: "basemap",
      mode: basemapVisibility,
      basemapSource: bmap,
    };
    dispatch(actionLayerUpdate({ type: "graphic", data }));
  };

  return (
    <Collapse in={expanded}>
      <Grid container sx={root_}>
        <RadioGroup
          aria-label="Select map or satellite as basemap"
          row
          value={basemapValue}
          onChange={handleChange}
        >
          <FormControlLabel
            value="map"
            label={<Typography variant="overline">Map</Typography>}
            labelPlacement="start"
            control={<Radio color="primary" />}
          />
          <FormControlLabel
            value="satellite"
            label={<Typography variant="overline">Satellite</Typography>}
            labelPlacement="start"
            control={<Radio color="primary" />}
          />
          <IconButton
            aria-label="Read more about basemaps"
            sx={iconButton_}
            onClick={() => setDialogOpen(!dialogOpen)}
            size="small"
          >
            <InfoIcon />
          </IconButton>
        </RadioGroup>
      </Grid>
      <Collapse in={dialogOpen}>
        <Typography variant="body2" sx={satelliteInfo_}>
          Satellite imagery is sourced by Mapbox, varies by location, is updated
          on an irregular schedule, and cannot be guaranteed to be up-to-date.
        </Typography>
      </Collapse>
    </Collapse>
  );
}
